// The typed feature-flag registry (contracts.md row 6; ADR-0012).
//
// One const-keyed registry, no magic strings: a typo is a compile error, not a
// silently-off flag. Each entry is {key, owner, kind, default, expiry?}. Evaluation is
// OpenFeature-shaped: a mandatory default baked into the registry, a stable evaluation
// context, and an observable reason code - never a silent guess.
//
// Per-kind failure semantics (feature-flags §2):
//   - release: default OFF, FAILS CLOSED  (a risky/unfinished path stays closed on error)
//   - kill:    default ON,  FAILS OPEN    (a flag-store blip must not black out the product)
//   - experiment: fails to its default (the control)
//   - permission: default OFF, FAILS CLOSED (deny)
//
// A flag may NEVER gate an auth, isolation, or hard-gate check. `assertFlagRegistryValid`
// enforces this at boot by reserving the security namespaces (see below).
import { z } from "zod";
/** The deploy environment a flag resolves against (00-architecture §6, D73). */
export const DeployEnv = z.enum(["development", "staging", "production"]);
export const FLAG_KINDS = ["release", "kill", "experiment", "permission"];
/**
 * The registry. Seeded with the release switch for each seam's real implementation
 * (fixture now, real behind the flag later) plus the model-gateway kill switch.
 */
export const FLAGS = {
    "gateway.real_provider": {
        key: "gateway.real_provider",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "vault.kms_real": {
        key: "vault.kms_real",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "retrieval.qdrant_real": {
        key: "retrieval.qdrant_real",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "runner.real_executor": {
        // Slice 1: swaps the fixture RunnerExecutor for the real microVM/gVisor container
        // (ADR-0014). Default OFF, fail-closed - the real executor is unavailable until a
        // runner host exists, so turning this on before then fails closed, never the fixture.
        key: "runner.real_executor",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "connections.real_git_host": {
        // Slice 2: swaps the fixture GitHostClient for the real GitHub App / GitLab OAuth adapter
        // (ADR-0016 §5). Default OFF, fail-closed - no real GitHub App creds exist yet (owner supply),
        // so turning this on before then fails closed to the fixture, never a broken live call.
        key: "connections.real_git_host",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "connections.real_cloud_federation": {
        // Slice 2: swaps the fixture CloudFederationClient for real customer-cloud federation.
        key: "connections.real_cloud_federation",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "connections.real_fingerprint": {
        // Slice 3: swaps the Slice-2 fixture FingerprintDetector for real repo detection (ADR-0016 §3).
        key: "connections.real_fingerprint",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "knowledge.falkor_real": {
        // Slice 3: swaps the fixture GraphStore for the real FalkorDB graph store (ADR-0019; D40).
        // Default OFF, fail-closed - no FalkorDB instance exists yet, so turning this on before then
        // fails closed to the fixture (a derived, rebuildable projection of the Postgres source,
        // D40/D41), never a broken live graph call. The "knowledge" namespace is not reserved, so
        // assertFlagRegistryValid still passes.
        key: "knowledge.falkor_real",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "certify.real_health_checks": {
        // Slice 4: swaps the fixture health clients (DomainSslClient, EmailDeliverabilityClient,
        // PaymentsHealthClient) for the real network-probing implementations. Default OFF, fail-closed -
        // a real DNS/TLS/SMTP probe reaches the network and must not run until the SSRF wall + egress
        // allowlist are in place (app-security §2), so this fails closed to the fixture, never a live
        // probe. "certify" is not a reserved security namespace, so assertFlagRegistryValid still passes.
        key: "certify.real_health_checks",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "certify.real_cert_verify": {
        // Slice 4: swaps the fixture CertificateVerifier for real certificate verification (chain +
        // revocation). Default OFF, fail-closed - no verification backend exists yet, so this fails
        // closed to the fixture, never a broken live verify call.
        key: "certify.real_cert_verify",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "storage.scaleway_real": {
        // Slice 4: swaps the fixture ObjectStorageClient for real Scaleway object storage (private,
        // encrypted, signed-URL). Default OFF, fail-closed - no Scaleway bucket/creds exist yet (owner
        // supply), so this fails closed to the fixture, never a broken live storage call. "storage" is
        // not a reserved security namespace, so the registry stays valid.
        key: "storage.scaleway_real",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "certify.public_trust_page": {
        // Slice 4: exposes the PUBLIC, unauthenticated trust page (GET /v1/trust/:slug). Default OFF,
        // fail-closed - a public surface must stay dark until it is complete and hardened (noindex,
        // headers, rate limit); a flag-store blip leaves it OFF, never accidentally public.
        key: "certify.public_trust_page",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "validate.real_research": {
        // Slice 5: swaps the fixture ResearchClient for real, cited research (market/demand/competitor
        // data). Default OFF, fail-closed - real research reaches external sources over the network and must
        // not run until the egress/SSRF wall is in place, so this fails closed to the fixture, never a live
        // fetch. "validate" is not a reserved security namespace, so assertFlagRegistryValid still passes.
        key: "validate.real_research",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "validate.real_upload_scan": {
        // Slice 5: swaps the fixture UploadScanner/AbuseClassifier for the real malware/injection scanner
        // (D77). Default OFF, fail-closed - the hardened-upload wall must never fall open; a flag-store blip
        // leaves the real scanner OFF (fixture blocks), never a live scan path that could admit an unscanned
        // file. This flag swaps the IMPLEMENTATION only; it never gates the security decision itself.
        key: "validate.real_upload_scan",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "brand.higgsfield_real": {
        // Slice 5: swaps the fixture MediaClient for real Higgsfield brand/mockup generation (D32). Default
        // OFF, fail-closed - no Higgsfield creds exist yet (owner supply), so this fails closed to the
        // fixture, never a broken live media call. "brand" is not a reserved security namespace.
        key: "brand.higgsfield_real",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "build.real_executor": {
        // Slice 5: swaps the fixture BuildOrchestrator for the real portable-build executor (D34). Default
        // OFF, fail-closed - no build runner host exists yet, so turning this on before then fails closed to
        // the fixture, never a broken live build. "build" is not a reserved security namespace.
        key: "build.real_executor",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "money.real_billing_import": {
        // Slice 6: swaps the fixture BillingImporter for real cloud-bill/Stripe reads (ADR-0023
        // decision 8; Slice 7). Default OFF, fail-closed - no real billing connector/creds exist yet
        // (owner supply), so this fails closed to the fixture, never a broken live import. This flag
        // swaps the IMPLEMENTATION only; it never gates the D27 cost wall itself (ADR-0023 decision 8).
        key: "money.real_billing_import",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "feedback.real_clusterer": {
        // Slice 6: swaps the fixture ThemeClusterer for real embedding clustering over RetrievalClient
        // (D45; ADR-0023 decision 8). Default OFF, fail-closed - this fails closed to the fixture, never
        // a broken live clustering call. "feedback" is not a reserved security namespace.
        key: "feedback.real_clusterer",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "pulse.real_anomaly": {
        // Slice 6: swaps the fixture AnomalyDetector for a real anomaly/churn detector (D45; ADR-0023
        // decision 8). Default OFF, fail-closed - this fails closed to the deterministic fixture, never
        // a broken live detector. "pulse" is not a reserved security namespace.
        key: "pulse.real_anomaly",
        owner: "tech-lead",
        kind: "release",
        default: false,
        expiry: "2026-12-31",
    },
    "kill.model_gateway": {
        key: "kill.model_gateway",
        owner: "tech-lead",
        kind: "kill",
        default: true,
    },
};
/** The safe value to serve when evaluation errors, chosen by the flag's kind. */
const failValue = (entry) => {
    switch (entry.kind) {
        case "kill":
            return true; // fail open: the feature stays available
        case "experiment":
            return entry.default; // serve the control
        case "release":
        case "permission":
            return false; // fail closed
    }
};
/**
 * Evaluate a flag server-side. Applies the per-kind failure semantics: a resolver
 * error never leaks through as an ambiguous value - it fails closed (release/permission),
 * open (kill), or to the control (experiment), and the reason is `ERROR`.
 */
export const evaluate = (key, ctx, resolver) => {
    const entry = FLAGS[key];
    if (!resolver)
        return { value: entry.default, reason: "DEFAULT" };
    const resolved = resolver(key, ctx);
    if (resolved === undefined)
        return { value: entry.default, reason: "DEFAULT" };
    if (resolved.ok)
        return { value: resolved.value, reason: "STATIC" };
    return { value: failValue(entry), reason: "ERROR", errorCode: resolved.error.code };
};
/** Convenience accessor (OpenFeature `getBooleanValue` shape): the resolved value only. */
export const getBooleanValue = (key, ctx, resolver) => evaluate(key, ctx, resolver).value;
// --- Boot assertions (feature-flags §5; ADR-0012) ---
/**
 * Security namespaces no flag may occupy. Reserving them is how "a flag may never gate
 * an auth/isolation/hard-gate check" becomes a testable boot assertion: a flag cannot
 * be introduced under these prefixes, so it cannot become the switch on a security gate.
 */
const RESERVED_SECURITY_PREFIXES = [
    "auth",
    "authn",
    "authz",
    "isolation",
    "rls",
    "tenant",
    "session",
    "hard_gate",
    "hardgate",
];
/** Throw if any flag occupies a reserved security namespace. */
export const assertNoSecurityGatingFlags = (registry) => {
    for (const flagKey of Object.keys(registry)) {
        const namespace = flagKey.split(".")[0] ?? flagKey;
        if (RESERVED_SECURITY_PREFIXES.includes(namespace)) {
            throw new Error(`flag "${flagKey}" occupies reserved security namespace "${namespace}"; a flag may never gate an auth/isolation/hard-gate check (ADR-0012)`);
        }
    }
};
/** Throw if a registry entry's declared default contradicts its kind's failure semantics, or its key is inconsistent. */
export const assertRegistryDefaults = (registry) => {
    for (const [mapKey, entry] of Object.entries(registry)) {
        if (entry.key !== mapKey) {
            throw new Error(`flag registry key mismatch: "${mapKey}" holds entry.key "${entry.key}"`);
        }
        if (entry.kind === "release" && entry.default) {
            throw new Error(`release flag "${mapKey}" must default OFF (fail closed)`);
        }
        if (entry.kind === "kill" && !entry.default) {
            throw new Error(`kill flag "${mapKey}" must default ON (fail open)`);
        }
    }
};
/** Run every registry invariant. Call once at boot; a violation is a programming error (fail fast). */
export const assertFlagRegistryValid = (registry = FLAGS) => {
    assertNoSecurityGatingFlags(registry);
    assertRegistryDefaults(registry);
};
//# sourceMappingURL=flags.js.map